/**
 * Which languages exist, and nothing about what they say. SPEC.md §17.1, §44.
 *
 * **This file is split from `index.ts` for one reason and it is a bundle
 * reason.** The language picker lives on the landing screen, which every device
 * loads before it has decided whether it wants a tutorial at all — and the
 * picker needs four names and four flags. If it imported the registry that
 * *also* holds the catalogues, the landing bundle would carry every string of
 * every chapter in four languages, some 60 kB of text, to draw four circles.
 *
 * So the metadata is here, the catalogues are in `index.ts`, and only the
 * tutorial chunk imports the second. The two are kept in step by
 * `i18n.test.ts`, which asserts that every locale listed here has a catalogue
 * behind it — because the failure mode otherwise is a fifth orb that opens a
 * tutorial written entirely in English.
 */

export type LocaleCode = 'en' | 'zh-Hans' | 'vi' | 'th';

/** The region whose flag stands for the language on the picker. */
export type FlagCode = 'gb' | 'cn' | 'vn' | 'th';

export interface LocaleMeta {
  code: LocaleCode;
  /**
   * The language's name **in that language**, and it is never translated. A
   * picker that labelled Thai as "Thai" would be a picker only an English
   * speaker could use — which is precisely the person who does not need it. §19
   */
  endonym: string;
  /** For the accessible name, which is read in the app's own language. */
  englishName: string;
  flag: FlagCode;
}

export const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * The four, in the order the picker draws them.
 *
 * Adding a fifth is this array plus one catalogue file plus one line in
 * `CATALOGS`. Traditional Chinese is the obvious next one: the catalogue is
 * `Partial`, so it would ship whatever is translated and read Simplified's
 * English fallback for the rest until it is finished.
 */
export const LOCALES: readonly LocaleMeta[] = [
  { code: 'en', endonym: 'English', englishName: 'English', flag: 'gb' },
  { code: 'zh-Hans', endonym: '中文', englishName: 'Chinese', flag: 'cn' },
  { code: 'vi', endonym: 'Tiếng Việt', englishName: 'Vietnamese', flag: 'vn' },
  { code: 'th', endonym: 'ไทย', englishName: 'Thai', flag: 'th' },
];

export function isLocaleCode(value: string): value is LocaleCode {
  return LOCALES.some((locale) => locale.code === value);
}

export function localeMeta(code: string | null | undefined): LocaleMeta {
  return LOCALES.find((locale) => locale.code === code) ?? LOCALES[0]!;
}

/**
 * The BCP 47 tag for the `lang` attribute, which is doing real work rather than
 * being metadata. §19
 *
 * It picks the font the platform uses for Han characters and — decisively for
 * Thai — turns on the line-breaking dictionary. Thai is written without spaces
 * between words, so a browser that has not been told the text is Thai breaks it
 * anywhere at all, mid-word, and the result is unreadable rather than untidy.
 */
export function htmlLang(code: LocaleCode): string {
  return code;
}
